/*
 * GoalList.jsx 02.08.18
 *
 * (c) 2018 ergovia GmbH
 */

import React from 'react';

import Check from '@material-ui/icons/Check';
import FilterListIcon from '@material-ui/icons/FilterList';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import IconButton from '@material-ui/core/IconButton';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import {withStyles} from '@material-ui/core/styles';

import Notification from '../core/Notification';
import GoalListItem from './GoalListItem';
import {FILTER_COMPLETED, FILTER_NO_ACTION_NEEDED, FILTER_NONE} from '../../actions/types';
import {COLOR_CODE_POSITIVE} from '../../styles/colors';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {activateFilter} from '../../actions/actions';


const styles = theme => ({
    toolbarTitle: {
        alignSelf: 'flex-start',
        margin: '18px 4px 0 4px',
        width: '50%'
    },
    toolbarText: {
        textTransform: 'uppercase'
    },

    filterMenu: {
        color: theme.palette.text.primary,
        position: 'absolute',
        top: 0,
        right: 0
    },

    notificationBox: {
        width: '90%',
        minHeight: 50,
        margin: '0.8rem auto',
        borderRadius: 4,

        background: 'repeating-linear-gradient(45deg, transparent, transparent 3px, #4B4B5D 3px, #4B4B5D 10px)',
        boxShadow: 'none',

        color: theme.palette.text.primary
    },

    notificationBoxContent: {
        padding: '1rem 1.5rem'
    },

    listContainer: {
        overflowY: 'auto',
        height: '80vh',
        '@media (max-height: 975px)': {
            height: '78vh'
        },
        '@media (max-height: 885px)': {
            height: '76vh'
        },
        '@media (max-height: 805px)': {
            height: '74vh'
        },
        '@media (max-height: 700px)': {
            height: '72vh'
        },
        '@media (max-height: 600px)': {
            height: '70vh'
        },
        '@media (max-height: 520px)': {
            height: '68vh'
        }
    },
    zone: {
        margin: '50px 0 -10px 30px',
        textTransform: 'uppercase',
        fontSize: 15
    },
    menuItemSelected: {
        backgroundColor: '#727388 !important'
    },
    selectedFilter: {
        marginRight: '5%',
        fontSize: 14,
        width: '61%',
        textAlign: 'right',
        alignSelf: 'flex-start',
        marginTop: '15px'
    }
});


/**
 * Komponente für die Darstellung der offenen Handlungbedarfe oberhalb der Auflistung der Ziele.
 */
const NotificationSummary = withStyles(styles)(props => {

    const {classes, notifications, hasGap} = props;

    if (notifications.length === 0 && !hasGap) {
        return (
            <Card className={classes.notificationBox}>
                <CardContent className={classes.notificationBoxContent}>
                    <Typography color="textPrimary"><Check style={{verticalAlign: -5, color: COLOR_CODE_POSITIVE}}/>Sehr gut, kein Handlungsbedarf!</Typography>
                </CardContent>
            </Card>
        );
    }

    const numHints = notifications.filter(n => n.type === 'hint').length,
        // +1 weil hasGaps als zusätzlicher Warnhinweis gilt
        numWarnings = notifications.filter(n => n.type === 'warning').length + (hasGap ? 1 : 0),
        msgHint = numHints === 1 ? 'Es liegt ein Hinweis vor.' : `Es liegen ${numHints} Hinweise vor.`,
        msgWarning = numWarnings === 1 ? 'Es liegt eine Warnmeldung vor.' : `Es liegen ${numWarnings} Warnmeldungen vor.`;

    return (
        <Card className={classes.notificationBox}>
            <CardContent className={classes.notificationBoxContent}>
                {numWarnings > 0 && <Notification type='warning' message={msgWarning}/>}
                {numHints > 0 && <Notification type='hint' message={msgHint}/>}
            </CardContent>
        </Card>
    );
});

/**
 * Komponente zur Darstellung eines Handlungsbedarfs innerhalb der Zielliste als eigene Karte, d.h. allgemeine Handlungsbedarfe, die keine konkreten Ziel zugeordnet werden können.
 */
const NotificationBox = withStyles(styles)(props => {

    const {classes, notifications} = props;

    if (!notifications || notifications.length === 0) {
        return null;
    }

    return (
        <Card className={classes.notificationBox}>
            <CardContent className={classes.notificationBoxContent}>
                {notifications.map((n, index) => <Notification key={index} type={n.type} message={n.message}/>)}
            </CardContent>
        </Card>
    );
});

/**
 * Komponente zur ANzeige des aktuell ausgewählten Filters
 */
const SelectedFilter = withStyles(styles)(props => {

    const {filter, classes} = props;

    switch (filter) {
        case FILTER_NO_ACTION_NEEDED:
            return <Typography variant="body1" className={classes.selectedFilter}>zeige nur Ziele mit Handlungsbedarf</Typography>;

        case FILTER_COMPLETED:
            return <Typography variant="body1" className={classes.selectedFilter}>zeige nur offene Ziele</Typography>;

        default:
            return <Typography variant="body1" className={classes.selectedFilter}>zeige alle Ziele</Typography>;
    }
});


/**
 * Komponente für die Darstellung der Zielliste inklusive der Filteroptionen.
 */
class GoalList extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            anchor: null
        };
    }

    /**
     * Behandelt den Klick auf den Filter-Button
     *
     * @param {object} event das Klick-Event
     */
    handleFilterClick(event) {
        this.setState({anchor: event.currentTarget});
    }

    /**
     * Behandelt die Auswahl einer Filteroption
     *
     * @param {object} filter die ausgewählte Filteroption
     */
    handleFilterClose(filter) {
        this.setState({anchor: null});

        if (typeof filter === 'string') {
            this.props.activateFilter(filter);
        }
    }

    render() {
        const {classes, participant, selectedGoal, goalList, notificationMap, hasGap, filter} = this.props;

        const notificationList = [];
        Array.from(notificationMap.values()).forEach(n => {

            if (Array.isArray(n)) {
                n.forEach(sub => notificationList.push(sub));
            } else {
                notificationList.push(n);
            }

        });


        const unspecificNotificationList = notificationMap.get(0);

        const finishedGoalsList = goalList.filter(g => g.zielerreicht);

        return (
            <div>
                <Toolbar variant="dense">
                    <Typography variant="title" className={classes.toolbarTitle}>{participant.nachname}, {participant.vorname}</Typography>


                    <SelectedFilter filter={filter}/>
                    <IconButton className={classes.filterMenu} aria-label="Filterung" aria-owns="filtermenu" aria-haspopup="true" onClick={this.handleFilterClick.bind(this)}>
                        <FilterListIcon/>
                    </IconButton>

                    <Menu id="filtermenu" anchorEl={this.state.anchor} open={Boolean(this.state.anchor)} onClose={this.handleFilterClose.bind(this)}>
                        <MenuItem classes={{selected: classes.menuItemSelected}} selected={filter === FILTER_NONE} onClick={() => this.handleFilterClose(FILTER_NONE)}>
                            zeige alle Ziele
                        </MenuItem>
                        <MenuItem classes={{selected: classes.menuItemSelected}} selected={filter === FILTER_COMPLETED} onClick={() => this.handleFilterClose(FILTER_COMPLETED)}>
                            zeige nur offene Ziele
                        </MenuItem>
                        <MenuItem classes={{selected: classes.menuItemSelected}} selected={filter === FILTER_NO_ACTION_NEEDED}
                            onClick={() => this.handleFilterClose(FILTER_NO_ACTION_NEEDED)}>
                            zeige nur Ziele mit Handlungsbedarf
                        </MenuItem>
                    </Menu>
                </Toolbar>

                <div className={classes.listContainer}>
                    <NotificationSummary hasGap={hasGap} notifications={notificationList}/>

                    <Typography classes={{root: classes.zone}}>Offene Ziele</Typography>

                    <NotificationBox notifications={unspecificNotificationList}/>

                    {goalList.filter(g => !g.zielerreicht).map(g => {

                        if (this.props.filter === FILTER_NO_ACTION_NEEDED && !notificationMap.get(g.id)) {
                            return null;
                        }

                        return <GoalListItem onSelect={this.props.onSelectGoal.bind(this)} key={g.id} data={g} notifications={notificationMap.get(g.id)}
                            selected={selectedGoal && selectedGoal.id === g.id}/>;
                    })}

                    <div style={{
                        display: this.props.filter === FILTER_COMPLETED || this.props.filter === FILTER_NO_ACTION_NEEDED || !finishedGoalsList.length ? 'none' : 'block'
                    }}>
                        <Typography classes={{root: classes.zone}}>Geschlossene Ziele</Typography>
                        {finishedGoalsList.map(g =>
                            <GoalListItem onSelect={this.props.onSelectGoal.bind(this)} key={g.id} data={g} notifications={notificationMap.get(g.id)}
                                selected={selectedGoal && selectedGoal.id === g.id}/>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

const mapDispatchToProps = {
    activateFilter
};

const mapStateToProps = ({filter}) => ({filter});

export default compose(connect(mapStateToProps, mapDispatchToProps), withStyles(styles))(GoalList);



// WEBPACK FOOTER //
// ./src/de/ergovia/components/detail/GoalList.jsx